/**
 * Date range helpers and CSV export for appointment filters (Option B).
 * All dates in YYYY-MM-DD. Week boundaries use local calendar (Mon–Sun) so
 * "this week" and "upcoming" overlap correctly for events in the current week.
 */

import { Appointment } from './appointmentManager';

/** Format a Date as local YYYY-MM-DD (avoids UTC shift making "today" or week wrong). */
function toLocalYYYYMMDD(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function getTodayString(): string {
  return toLocalYYYYMMDD(new Date());
}

export function getYesterdayString(): string {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return toLocalYYYYMMDD(d);
}

/** Monday of current week (local). */
export function getStartOfWeekString(): string {
  const d = new Date();
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  d.setDate(diff);
  return toLocalYYYYMMDD(d);
}

/** Sunday of current week (local). */
export function getEndOfWeekString(): string {
  const d = new Date();
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  d.setDate(diff);
  d.setDate(d.getDate() + 6);
  return toLocalYYYYMMDD(d);
}

export function getEndOfYesterdayString(): string {
  return getYesterdayString();
}

/** For archive: end date = end of yesterday. Start = old sentinel. */
export const ARCHIVE_START_DATE = '2000-01-01';

export type BusinessDateFilter = 'today' | 'yesterday' | 'this_week' | 'archive';
export type CustomerDateFilter = 'upcoming' | 'today' | 'yesterday' | 'this_week' | 'past';

export function getDateRangeForBusinessFilter(filter: BusinessDateFilter): { startDate: string; endDate: string } {
  const today = getTodayString();
  const yesterday = getYesterdayString();
  const startWeek = getStartOfWeekString();
  const endWeek = getEndOfWeekString();
  switch (filter) {
    case 'today':
      return { startDate: today, endDate: today };
    case 'yesterday':
      return { startDate: yesterday, endDate: yesterday };
    case 'this_week':
      return { startDate: startWeek, endDate: endWeek };
    case 'archive':
      return { startDate: ARCHIVE_START_DATE, endDate: yesterday };
    default:
      return { startDate: today, endDate: today };
  }
}

/** Same as UserDashboardScreen getUpcomingAppointments(): statuses that count as "upcoming". */
const UPCOMING_STATUSES = new Set([
  'confirmed',
  'pending',
  'pending_business_confirmation',
  'pending_reschedule',
  'paid',
]);

/** Normalize date to YYYY-MM-DD for comparison (handles Date objects or alternate string formats). */
function toYYYYMMDD(dateVal: string | undefined | null): string {
  if (dateVal == null || dateVal === '') return '';
  if (typeof dateVal === 'string' && /^\d{4}-\d{2}-\d{2}/.test(dateVal)) return dateVal.substring(0, 10);
  try {
    const d = new Date(dateVal);
    if (!isNaN(d.getTime())) {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    }
  } catch (_) {}
  return String(dateVal);
}

/** Filter customer appointments by view (in-memory). Matches UserDashboardScreen getUpcomingAppointments() for "upcoming". */
export function getFilteredCustomerAppointments(
  appointments: Appointment[],
  filter: CustomerDateFilter
): Appointment[] {
  const today = getTodayString();
  const yesterday = getYesterdayString();
  const startWeek = getStartOfWeekString();
  const endWeek = getEndOfWeekString();
  const excludeCancelled = (a: Appointment) => a.status !== 'cancelled';
  let list: Appointment[];
  switch (filter) {
    case 'upcoming':
      list = appointments
        .filter((a) => {
          const status = (a as any).status ?? a.status;
          const dateStr = toYYYYMMDD(a.appointmentDate);
          return (
            UPCOMING_STATUSES.has(status) &&
            dateStr >= today
          );
        })
        .sort((a, b) => {
          const da = toYYYYMMDD(a.appointmentDate);
          const db = toYYYYMMDD(b.appointmentDate);
          return da.localeCompare(db) || (a.appointmentTime || '').localeCompare(b.appointmentTime || '');
        });
      break;
    case 'today':
      list = appointments.filter((a) => excludeCancelled(a) && toYYYYMMDD(a.appointmentDate) === today)
        .sort((a, b) => (a.appointmentTime || '').localeCompare(b.appointmentTime || ''));
      break;
    case 'yesterday':
      list = appointments.filter((a) => excludeCancelled(a) && toYYYYMMDD(a.appointmentDate) === yesterday)
        .sort((a, b) => (a.appointmentTime || '').localeCompare(b.appointmentTime || ''));
      break;
    case 'this_week':
      // Mon–Sun (local); includes upcoming events that fall in this week
      list = appointments
        .filter((a) => {
          const dateStr = toYYYYMMDD(a.appointmentDate);
          return excludeCancelled(a) && dateStr >= startWeek && dateStr <= endWeek;
        })
        .sort((a, b) => toYYYYMMDD(a.appointmentDate).localeCompare(toYYYYMMDD(b.appointmentDate)) || (a.appointmentTime || '').localeCompare(b.appointmentTime || ''));
      break;
    case 'past':
      list = appointments
        .filter((a) => excludeCancelled(a) && toYYYYMMDD(a.appointmentDate) < today)
        .sort((a, b) => toYYYYMMDD(b.appointmentDate).localeCompare(toYYYYMMDD(a.appointmentDate)));
      break;
    default:
      list = appointments.filter(excludeCancelled);
  }
  return list;
}

/** Build CSV string for appointments (business view: customer-focused columns). */
export function appointmentsToCSV(appointments: Appointment[], isBusinessView: boolean): string {
  const header = isBusinessView
    ? 'Date,Time,Customer,Phone,Email,Service,Status,Address'
    : 'Date,Time,Business,Service,Status,Address';
  const escape = (v: string) => {
    const s = String(v ?? '');
    if (s.includes(',') || s.includes('"') || s.includes('\n')) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };
  const rows = appointments.map((a) => {
    if (isBusinessView) {
      return [
        a.appointmentDate,
        a.appointmentTime || '',
        a.customerName || '',
        a.phone || '',
        a.email || '',
        a.serviceType || '',
        a.status || '',
        (a.address || '').replace(/\n/g, ' '),
      ].map(escape).join(',');
    }
    return [
      a.appointmentDate,
      a.appointmentTime || '',
      a.businessName || '',
      a.serviceType || '',
      a.status || '',
      (a.businessAddress || a.address || '').replace(/\n/g, ' '),
    ].map(escape).join(',');
  });
  return [header, ...rows].join('\n');
}

/** Trigger download of CSV (web: blob download; RN: share or leave in state). */
export function downloadCSV(csv: string, filename: string): void {
  if (typeof window === 'undefined' || !window.URL || !window.navigator) return;
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  window.URL.revokeObjectURL(url);
}

/** Local calendar YYYY-MM-DD for an appointment date string (reuses internal normalizer). */
export function toAppointmentLocalDateKey(dateVal: string | undefined | null): string {
  return toYYYYMMDD(dateVal);
}

export type DateSectionGroup<T> = { dateKey: string; title: string; items: T[] };

function formatDateSectionHeading(dateKey: string): string {
  const parts = dateKey.split('-');
  if (parts.length === 3) {
    const y = parseInt(parts[0], 10);
    const m = parseInt(parts[1], 10) - 1;
    const d = parseInt(parts[2], 10);
    if (!Number.isNaN(y) && !Number.isNaN(m) && !Number.isNaN(d)) {
      const date = new Date(y, m, d);
      return date.toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    }
  }
  return dateKey;
}

/**
 * Groups appointments under one heading per local calendar day.
 * Items within each day are sorted by appointmentTime then id.
 */
export function groupAppointmentsIntoDateSections<T extends { id: string; appointmentDate: string; appointmentTime?: string }>(
  appointments: T[],
  options?: { descending?: boolean }
): DateSectionGroup<T>[] {
  const map = new Map<string, T[]>();
  for (const a of appointments) {
    const key = toYYYYMMDD(a.appointmentDate);
    if (!key) continue;
    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push(a);
  }
  for (const list of map.values()) {
    list.sort(
      (x, y) =>
        (x.appointmentTime || '').localeCompare(y.appointmentTime || '') || x.id.localeCompare(y.id)
    );
  }
  const keys = Array.from(map.keys()).sort((a, b) =>
    options?.descending ? b.localeCompare(a) : a.localeCompare(b)
  );
  return keys.map((dateKey) => ({
    dateKey,
    title: formatDateSectionHeading(dateKey),
    items: map.get(dateKey)!,
  }));
}
